import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import apis from '@src/apis';
import ROUTES from '@src/constants/route';
import { TOP_PAID_EVENT_CODES_HASH } from '@src/constants/topPaidEvent';
import actions from '@src/redux/actions';
import ProcessHandler from '@src/components/ProcessHandler';
import useTopPaidEvent from '@src/hooks/useTopPaidEvent';
import {
  calcAmountToNextCode,
  countPaidTransactions,
  sumAccumulation,
  vndToUsd,
} from '@src/services/topPaidEvent';
import { delimitNumber } from '@src/utils/number';
import { checkVietNam } from '@src/utils/checkCountry';
import EventHeader from './EventHeader';
import AccumulationCard from './AccumulationCard';
import PrizeSection from './PrizeSection';
import RuleSection from './RuleSection';
import RewardCodeList from './RewardCodeList';
import { StyledTopPaidEvent } from './index.style';

const TopPaidEvent = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { config, isRunning, isDetailPageOpen } = useTopPaidEvent();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [codes, setCodes] = useState([]);
  const [usdToVndRate, setUsdToVndRate] = useState(null);
  const codeListRef = useRef(null);
  const isVietNam = checkVietNam();

  const eventId = config?.eventId;
  const startTime = config?.startTime;
  const endTime = config?.endTime;

  useEffect(() => {
    if (!eventId || !isDetailPageOpen) return undefined;

    let ignore = false;
    const fetchEventData = async () => {
      setLoading(true);
      const [ordersRes, codesRes, rateRes] = await Promise.all([
        apis.topPaidEvent
          .getEventPaidOrders({ startTime, endTime })
          .catch(() => null),
        apis.topPaidEvent.getRewardCodes(eventId).catch(() => null),
        isVietNam ? null : apis.banks.getUSDToVNDRate().catch(() => null),
      ]);
      if (ignore) return;
      // A failed load would otherwise read as ₫0 and no codes.
      if (!ordersRes?.status || !codesRes?.status)
        dispatch(
          actions.noti.push({ severity: 'error', message: 'errorOccurred' }),
        );
      setOrders(ordersRes?.status ? ordersRes.result?.orders || [] : []);
      setCodes(codesRes?.status ? codesRes.result?.rewardCodes || [] : []);
      setUsdToVndRate(
        rateRes?.status && rateRes.result > 0 ? rateRes.result : null,
      );
      setLoading(false);
    };
    fetchEventData();

    return () => {
      ignore = true;
    };
  }, [eventId, startTime, endTime, isDetailPageOpen, isVietNam, dispatch]);

  // "View my codes" lands here with the hash; scroll once the list has rendered.
  useEffect(() => {
    if (!loading && location.hash === TOP_PAID_EVENT_CODES_HASH)
      codeListRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [loading, location.hash, location.key]);

  // Same landing as PrivateRoute when the feature is off.
  if (!config || !isDetailPageOpen) return <Redirect to={ROUTES.TTS} />;

  // Foreign IPs wait for the rate so amounts never flash in VND before USD.
  if (!isVietNam && loading)
    return (
      <StyledTopPaidEvent>
        <ProcessHandler loading mt="40px" ml="0px" align="center" />
      </StyledTopPaidEvent>
    );

  const formatMoney = (amount) =>
    isVietNam || !usdToVndRate
      ? `₫${delimitNumber(amount, i18n.language)}`
      : `$${vndToUsd(amount, usdToVndRate).toLocaleString(i18n.language, {
          maximumFractionDigits: 2,
        })}`;
  const threshold = config.rewardCodeThreshold;
  const total = sumAccumulation(orders);
  const thresholdText = formatMoney(threshold);

  const scrollToCodes = () =>
    codeListRef.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <StyledTopPaidEvent>
      <EventHeader thresholdText={thresholdText} />
      <ProcessHandler loading={loading} mt="40px" ml="0px" align="center">
        <AccumulationCard
          config={config}
          isRunning={isRunning}
          totalText={formatMoney(total)}
          transactionCount={countPaidTransactions(orders)}
          codeCount={codes.length}
          amountToNextCode={calcAmountToNextCode(total, threshold)}
          formatMoney={formatMoney}
          onViewCodes={scrollToCodes}
        />
      </ProcessHandler>
      <PrizeSection images={config.images} />
      <RuleSection config={config} thresholdText={thresholdText} />
      {!loading && <RewardCodeList ref={codeListRef} codes={codes} />}
    </StyledTopPaidEvent>
  );
};

export default TopPaidEvent;
